import React, { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery } from '@apollo/react-hooks'
import { useSelector } from 'react-redux'

import { StateContext } from '../../../state'
import { ControlConsumer } from '../../../control'
import SettingsTable from './settings-table'
import PROFILE_PREFERENCES_QUERY from '../../../../../../graphql/queries/profile-preferences.gql'
import { isEpisode } from '../../../../../lib/content'
import { getSelectedProfileIdFromSession } from '../../../../../lib/account'

/**
 * Manages playback quality settings and user preferences in the video player.
 *
 * Key Features:
 * - Fetches user preferences when settings panel is opened
 * - Displays next-episode autoplay preference from profile
 * - Manages playback quality selection with upgrade notifications
 */
const SettingsPanel = ({ showQualityUpgradePopup }) => {
  const {
    playbackQualityOptions,
    isSettingsPanelOpen,
    currentQuality,
    toggleSettingsPanelOpen,
    contentItem,
    isSafari
  } = useContext(StateContext)
  const isEpisodeContent = isEpisode(contentItem)
  const profileId = useSelector(state => getSelectedProfileIdFromSession(state.session))

  const [fetchProfilePreferences,
    { data, loading, error }
  ] = useLazyQuery(PROFILE_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !isEpisodeContent
  })

  useEffect(() => {
    if (isSettingsPanelOpen && profileId) {
      fetchProfilePreferences()
    }
  }, [isSettingsPanelOpen, profileId])

  // Wait for profile preferences to load before rendering settings
  if ((loading || error || !data?.profilePreferences) && profileId) {
    return null
  }

  return (
    <ControlConsumer>
      {({ setQuality }) => (
        <SettingsTable
          {...{
            playbackQualityOptions,
            isSettingsPanelOpen,
            currentQuality,
            toggleSettingsPanelOpen,
            setQuality,
            nextEpisodeAutoplayEnabled: data?.profilePreferences?.nextEpisodeAutoplayEnabled
          }}
          showQualityUpgradePopup={showQualityUpgradePopup}
          isSafari={isSafari}
          isEpisode={isEpisodeContent}
        />
      )}
    </ControlConsumer>
  )
}

SettingsPanel.propTypes = {
  showQualityUpgradePopup: PropTypes.func.isRequired
}

export default SettingsPanel
